import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
    Play, Pause, ChevronRight, Monitor, Smartphone, Download,
    CheckCircle, Clock, Users, FileText, Calendar, Shield,
    Zap, Target, BarChart3, Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';

const tutorialSections = [
    {
        id: 'getting-started',
        title: 'Getting Started',
        duration: '2 min',
        icon: Monitor,
        steps: [
            {
                title: 'Accessing FendanaCor',
                description: 'Open your browser and navigate to the FendanaCor portal. You\'ll see the login screen with options to sign in or register.',
                image: '/tutorial/login.png',
                tips: ['Use your NHS email for registration', 'You\'ll need your USIC and CSIC codes from your organization']
            },
            {
                title: 'Dashboard Overview',
                description: 'After logging in, you\'ll see your personalized dashboard showing referral statistics, recent patients, and quick actions.',
                image: '/tutorial/dashboard.png',
                tips: ['Use the search bar to find specific patients', 'Color-coded badges indicate urgency levels']
            }
        ]
    },
    {
        id: 'creating-referral',
        title: 'Creating a Referral',
        duration: '5 min',
        icon: FileText,
        steps: [
            {
                title: 'Step 1: Patient Demographics',
                description: 'Enter patient details including NHS number, name, date of birth, and contact information. The system will calculate age automatically.',
                tips: ['NHS number helps with e-RS integration', 'Ensure contact email is correct for appointment notifications']
            },
            {
                title: 'Step 2: Body Part Selection',
                description: 'Select the affected body part from the visual diagram. Choose from Hip, Knee, Shoulder, Elbow, Spine, Hand/Wrist, or Foot/Ankle.',
                tips: ['Each body part has specific clinical guidelines', 'Selection determines which questionnaires are shown']
            },
            {
                title: 'Step 3: Condition Selection',
                description: 'Choose the specific orthopaedic condition from the dropdown. Options are filtered based on the selected body part.',
                tips: ['For complex cases, select the primary condition', 'Additional notes can capture secondary issues']
            },
            {
                title: 'Step 4: Medical History',
                description: 'Complete the comprehensive medical history including comorbidities, previous surgeries, and medications.',
                tips: ['Accurate history improves screening accuracy', 'Flag anticoagulants and immunosuppressants']
            },
            {
                title: 'Step 5: Treatment History',
                description: 'Document all conservative treatments tried. This is MANDATORY for most conditions - physiotherapy, analgesia, injections.',
                tips: ['Minimum 6 physio sessions required for most conditions', 'If no physio, provide clinical justification']
            },
            {
                title: 'Step 6: Clinical Questionnaire',
                description: 'Complete the relevant clinical questionnaire (Oxford Hip/Knee, SPADI, MEPS, etc.) to generate objective scores.',
                tips: ['Scores affect urgency calculation', 'Some conditions have minimum score thresholds']
            },
            {
                title: 'Step 7: Clinical Findings',
                description: 'Document examination findings. This is MANDATORY. Include ROM, power, sensation, and special tests.',
                tips: ['Positive neurological findings may auto-approve referral', 'Be thorough - this helps the consultant']
            },
            {
                title: 'Step 8: Review & Submit',
                description: 'Review all entered information and submit. The system will automatically screen against clinical guidelines.',
                tips: ['Check all sections before submitting', 'You can save as draft and return later']
            }
        ]
    },
    {
        id: 'understanding-screening',
        title: 'Understanding Screening',
        duration: '3 min',
        icon: Shield,
        steps: [
            {
                title: 'Automatic Screening',
                description: 'FendanaCor screens every referral against BOA, NICE, NHS, and specialty-specific guidelines automatically.',
                tips: ['Screening happens in real-time', 'Results show exactly which criteria passed or failed']
            },
            {
                title: 'Screening Results',
                description: 'Referrals are either Accepted or Rejected with detailed feedback. Accepted referrals proceed to scheduling.',
                tips: ['Rejected referrals include specific recommendations', 'Address failures and re-submit when ready']
            },
            {
                title: 'Urgency Scoring',
                description: 'Each referral receives an urgency score (0-100) determining wait time target: Urgent (6wk), Semi-urgent (13wk), Routine (26wk).',
                tips: ['Consultants can override urgency if needed', 'Red flags escalate to urgent automatically']
            }
        ]
    },
    {
        id: 'quick-referrals',
        title: 'Quick Referrals',
        duration: '1 min',
        icon: Zap,
        steps: [
            {
                title: 'Using Templates',
                description: 'For common conditions, use Quick Referral templates that pre-fill treatment history and clinical findings.',
                tips: ['Great for straightforward cases', 'Still verify all pre-filled data before submitting']
            },
            {
                title: 'Available Templates',
                description: 'Templates available for: Knee OA, Hip OA, Carpal Tunnel, Rotator Cuff, Hallux Valgus, and Spinal Stenosis.',
                tips: ['Templates assume conservative treatment completed', 'Customize as needed for specific patients']
            }
        ]
    },
    {
        id: 'consultant-features',
        title: 'For Consultants',
        duration: '4 min',
        icon: Users,
        steps: [
            {
                title: 'Consultant Dashboard',
                description: 'View referrals assigned to you, organized by status: Pending Review, Accepted, Scheduled.',
                tips: ['Only shows YOUR patients based on fair distribution', 'Use filters to find specific cases']
            },
            {
                title: 'Reviewing Referrals',
                description: 'Accept, reject, or request additional tests. You can override urgency levels when clinically indicated.',
                tips: ['Pre-clinic notes provide comprehensive summaries', 'Document your reasoning for rejections']
            },
            {
                title: 'Calendar & Scheduling',
                description: 'Use the drag-and-drop calendar to schedule appointments. The system auto-schedules based on urgency.',
                tips: ['Block dates for leave in advance', 'Add extra clinic dates for one-off sessions']
            },
            {
                title: 'Schedule Settings',
                description: 'Configure your clinic frequency, days, capacity, and subspecialties for fair patient distribution.',
                tips: ['Subspecialty matching ensures appropriate cases', 'Adjust capacity based on clinic type']
            }
        ]
    },
    {
        id: 'analytics',
        title: 'Analytics & Reporting',
        duration: '2 min',
        icon: BarChart3,
        steps: [
            {
                title: 'Analytics Dashboard',
                description: 'NHS Managers can view referral volumes, acceptance rates, wait times, and performance metrics.',
                tips: ['Filter by date range, condition, or urgency', 'Export data for Trust reporting']
            },
            {
                title: 'Audit Trail',
                description: 'Every action is logged for CQC compliance. Export complete audit trails as CSV.',
                tips: ['Includes who did what and when', 'Essential for governance reviews']
            },
            {
                title: 'PDF Exports',
                description: 'Generate PDF referral summaries for NHS e-RS submission or printing.',
                tips: ['Professional NHS-style formatting', 'Includes all clinical data and scores']
            }
        ]
    }
];

const videoScripts = {
    tutorial: {
        title: "FendanaCor Tutorial Video Script",
        duration: "3 minutes",
        scenes: [
            { time: "0:00-0:15", scene: "Opening", script: "Welcome to FendanaCor - the intelligent orthopaedic referral system designed for the NHS. In the next few minutes, we'll show you how easy it is to create evidence-based referrals." },
            { time: "0:15-0:30", scene: "Login", script: "Simply log in with your NHS credentials and subscription codes. Your personalized dashboard shows all your referrals at a glance with color-coded urgency indicators." },
            { time: "0:30-1:00", scene: "Dashboard", script: "The dashboard provides instant visibility into pending, accepted, and scheduled referrals. Search for any patient by name or NHS number, and filter by condition or urgency." },
            { time: "1:00-1:30", scene: "Creating Referral", script: "Creating a referral is straightforward. Enter patient demographics, select the affected body part, and choose the specific condition. Our guided form ensures you capture all clinically relevant information." },
            { time: "1:30-2:00", scene: "Treatment & Questionnaires", script: "Document conservative treatments tried - physiotherapy, analgesia, injections. Complete the relevant clinical questionnaire for objective scoring. The system validates against NHS guidelines automatically." },
            { time: "2:00-2:20", scene: "Auto Screening", script: "When you submit, FendanaCor instantly screens against BOA, NICE, and specialty guidelines. Appropriate referrals are accepted with urgency scores. If criteria aren't met, you receive specific recommendations." },
            { time: "2:20-2:40", scene: "Scheduling", script: "Accepted referrals are auto-scheduled to consultants based on subspecialty and availability. Patients receive professional appointment letters via email. Referring doctors are notified when appointments are booked." },
            { time: "2:40-3:00", scene: "Closing", script: "FendanaCor: Evidence-based triage, seamless workflow, better patient outcomes. Start streamlining your orthopaedic referrals today." }
        ]
    },
    advert: {
        title: "FendanaCor Advertisement Script",
        duration: "1 minute",
        scenes: [
            { time: "0:00-0:10", scene: "Problem", script: "NHS waiting lists are at record levels. 40% of orthopaedic referrals don't meet secondary care criteria. There has to be a better way." },
            { time: "0:10-0:25", scene: "Solution", script: "Introducing FendanaCor - intelligent referral triage that screens against clinical guidelines instantly. Only appropriate referrals reach consultants." },
            { time: "0:25-0:40", scene: "Benefits", script: "90% reduction in triage time. 40% fewer inappropriate referrals. Automatic scheduling. Complete audit trails. Real-time performance monitoring." },
            { time: "0:40-0:50", scene: "Outcome", script: "Patients get the right care at the right time. Consultants focus on clinical work. Trusts reduce waiting lists and costs." },
            { time: "0:50-1:00", scene: "CTA", script: "FendanaCor: Ortho referrals without pain. Request a demo at fendanacor.com" }
        ]
    }
};

export default function TutorialPage() {
    const [activeSection, setActiveSection] = useState('getting-started');
    const [activeStep, setActiveStep] = useState(0);

    const currentSection = tutorialSections.find(s => s.id === activeSection);

    return (
        <div className="min-h-screen bg-slate-50" data-testid="tutorial-page">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <img src="/logo.png" alt="FendanaCor" className="w-10 h-10 rounded-lg" />
                            <div>
                                <h1 className="font-semibold text-slate-900">FendanaCor Tutorials</h1>
                                <p className="text-xs text-slate-500">Learn how to use the system</p>
                            </div>
                        </div>
                        <Button variant="outline" onClick={() => window.location.href = '/dashboard'}>
                            Back to Dashboard
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Tabs defaultValue="guide" className="space-y-6">
                    <TabsList className="grid w-full max-w-md grid-cols-2">
                        <TabsTrigger value="guide">Interactive Guide</TabsTrigger>
                        <TabsTrigger value="scripts">Video Scripts</TabsTrigger>
                    </TabsList>

                    {/* Interactive Guide */}
                    <TabsContent value="guide">
                        <div className="grid grid-cols-12 gap-6">
                            {/* Sidebar Navigation */}
                            <div className="col-span-3">
                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg">Tutorial Sections</CardTitle>
                                    </CardHeader>
                                    <CardContent className="p-0">
                                        <nav className="space-y-1 p-2">
                                            {tutorialSections.map((section) => (
                                                <button
                                                    key={section.id}
                                                    onClick={() => {
                                                        setActiveSection(section.id);
                                                        setActiveStep(0);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                                                        activeSection === section.id
                                                            ? 'bg-teal-50 text-teal-700'
                                                            : 'hover:bg-slate-50 text-slate-700'
                                                    }`}
                                                >
                                                    <section.icon className={`w-5 h-5 ${
                                                        activeSection === section.id ? 'text-teal-600' : 'text-slate-400'
                                                    }`} />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium truncate">{section.title}</p>
                                                        <p className="text-xs text-slate-500">{section.duration}</p>
                                                    </div>
                                                </button>
                                            ))}
                                        </nav>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Main Content */}
                            <div className="col-span-9">
                                {currentSection && (
                                    <motion.div
                                        key={activeSection}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        <Card>
                                            <CardHeader>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                                                        <currentSection.icon className="w-6 h-6 text-teal-600" />
                                                    </div>
                                                    <div>
                                                        <CardTitle>{currentSection.title}</CardTitle>
                                                        <CardDescription>
                                                            Step {activeStep + 1} of {currentSection.steps.length}
                                                        </CardDescription>
                                                    </div>
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                {/* Progress */}
                                                <div className="flex gap-1 mb-6">
                                                    {currentSection.steps.map((_, i) => (
                                                        <div
                                                            key={i}
                                                            className={`h-1 flex-1 rounded ${
                                                                i <= activeStep ? 'bg-teal-500' : 'bg-slate-200'
                                                            }`}
                                                        />
                                                    ))}
                                                </div>

                                                {/* Step Content */}
                                                <div className="space-y-4">
                                                    <h3 className="text-xl font-semibold text-slate-900">
                                                        {currentSection.steps[activeStep].title}
                                                    </h3>
                                                    <p className="text-slate-600">
                                                        {currentSection.steps[activeStep].description}
                                                    </p>

                                                    {/* Tips */}
                                                    {currentSection.steps[activeStep].tips && (
                                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                                                            <h4 className="font-semibold text-amber-800 mb-2">Tips</h4>
                                                            <ul className="space-y-2">
                                                                {currentSection.steps[activeStep].tips.map((tip, i) => (
                                                                    <li key={i} className="flex items-start gap-2 text-sm text-amber-700">
                                                                        <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                                                        {tip}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {/* Navigation */}
                                                    <div className="flex justify-between pt-6">
                                                        <Button
                                                            variant="outline"
                                                            onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                                                            disabled={activeStep === 0}
                                                        >
                                                            Previous
                                                        </Button>
                                                        <Button
                                                            onClick={() => setActiveStep(Math.min(currentSection.steps.length - 1, activeStep + 1))}
                                                            disabled={activeStep === currentSection.steps.length - 1}
                                                        >
                                                            Next
                                                            <ChevronRight className="w-4 h-4 ml-1" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    {/* Video Scripts */}
                    <TabsContent value="scripts">
                        <div className="grid grid-cols-2 gap-6">
                            {/* Tutorial Script */}
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <CardTitle>{videoScripts.tutorial.title}</CardTitle>
                                            <CardDescription>Duration: {videoScripts.tutorial.duration}</CardDescription>
                                        </div>
                                        <Badge className="bg-teal-100 text-teal-700">Tutorial</Badge>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {videoScripts.tutorial.scenes.map((scene, i) => (
                                            <div key={i} className="border-l-2 border-teal-200 pl-4 py-2">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Badge variant="outline" className="text-xs">{scene.time}</Badge>
                                                    <span className="font-semibold text-slate-900">{scene.scene}</span>
                                                </div>
                                                <p className="text-sm text-slate-600 italic">&quot;{scene.script}&quot;</p>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Advertisement Script */}
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <CardTitle>{videoScripts.advert.title}</CardTitle>
                                            <CardDescription>Duration: {videoScripts.advert.duration}</CardDescription>
                                        </div>
                                        <Badge className="bg-purple-100 text-purple-700">Advertisement</Badge>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {videoScripts.advert.scenes.map((scene, i) => (
                                            <div key={i} className="border-l-2 border-purple-200 pl-4 py-2">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Badge variant="outline" className="text-xs">{scene.time}</Badge>
                                                    <span className="font-semibold text-slate-900">{scene.scene}</span>
                                                </div>
                                                <p className="text-sm text-slate-600 italic">&quot;{scene.script}&quot;</p>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Production Notes */}
                        <Card className="mt-6">
                            <CardHeader>
                                <CardTitle>Video Production Notes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-3 gap-6">
                                    <div>
                                        <h4 className="font-semibold text-slate-900 mb-2">Visual Style</h4>
                                        <ul className="text-sm text-slate-600 space-y-1">
                                            <li>• Clean, modern healthcare aesthetic</li>
                                            <li>• NHS-appropriate professional tone</li>
                                            <li>• Teal/white color palette</li>
                                            <li>• Smooth transitions between scenes</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-900 mb-2">Audio</h4>
                                        <ul className="text-sm text-slate-600 space-y-1">
                                            <li>• Professional voiceover (British accent)</li>
                                            <li>• Subtle background music</li>
                                            <li>• UI sound effects for clicks</li>
                                            <li>• Clear, confident narration pace</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-900 mb-2">Format</h4>
                                        <ul className="text-sm text-slate-600 space-y-1">
                                            <li>• Resolution: 1920x1080 (Full HD)</li>
                                            <li>• Frame rate: 30fps</li>
                                            <li>• Format: MP4 (H.264)</li>
                                            <li>• Subtitles: English (embedded)</li>
                                        </ul>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
